import { diagnosticBlock, diagnosticOutcome, tradeProviderInfo, tradeSpan } from "./trade-diagnostics";
import type { Eip1193Provider } from "./eip1193";

export const DEFAULT_RPC_READ_TIMEOUT_MS = 15_000;
export type RpcReadOptions = { signal?: AbortSignal; timeoutMs?: number; diagnosticOperationId?: string | undefined };
export type RpcReadMethod = "eth_chainId" | "eth_getCode" | "eth_getStorageAt" | "eth_getBlockByNumber" | "eth_getBlockByHash" |
  "eth_call" | "eth_getTransactionReceipt" | "eth_getTransactionByHash" | "eth_getLogs" |
  "eth_getBalance" | "eth_estimateGas" | "eth_gasPrice";

/** Bound observation only: a timed-out wallet send could still broadcast later. */
export function readRpc(provider: Eip1193Provider,
  args: { method: RpcReadMethod; params?: readonly unknown[] | object }, options: RpcReadOptions = {}): Promise<unknown> {
  return new Promise((resolve, reject) => {
    if (options.signal?.aborted) { reject(options.signal.reason); return; }
    const info = tradeProviderInfo(provider);
    const blockIndex = args.method === "eth_getBlockByNumber" ? 0 :
      ["eth_call", "eth_getCode", "eth_getBalance"].includes(args.method) ? 1 : -1;
    const block = Array.isArray(args.params) && blockIndex >= 0 ? args.params[blockIndex] : undefined;
    const end = tradeSpan(options.diagnosticOperationId ?? info.operationId, "rpc", { ...info, method: args.method, callCount: 1,
      ...(typeof block === "string" && /^(latest|pending|safe|finalized|0x[0-9a-f]+)$/i.test(block) ? { requestedBlock: block } : {}) });
    let settled = false;
    const finish = (error: boolean, value: unknown) => {
      if (settled) return;
      settled = true;
      end(error ? diagnosticOutcome(value) : "ok", !error &&
        ["eth_getTransactionReceipt", "eth_getBlockByNumber", "eth_getBlockByHash"].includes(args.method) ? diagnosticBlock(value) : {});
      clearTimeout(timer);
      options.signal?.removeEventListener("abort", onAbort);
      if (error) reject(value);
      else resolve(value);
    };
    const onAbort = () => finish(true, options.signal?.reason);
    const timer = setTimeout(() => finish(true, new Error("RPC_READ_TIMEOUT: The wallet RPC did not respond. Try the read again.")),
      options.timeoutMs ?? DEFAULT_RPC_READ_TIMEOUT_MS);
    options.signal?.addEventListener("abort", onAbort, { once: true });
    try {
      // EIP-1193 cannot cancel the provider request itself. Own both outcomes so
      // late resolution/rejection cannot publish state or become unhandled.
      provider.request(args).then((value) => finish(false, value), (error: unknown) => finish(true, error));
    } catch (error) { finish(true, error); }
  });
}
